import React from 'react'
import moment from 'moment'

import AirportShuttleIcon from '@mui/icons-material/AirportShuttle'
import BusinessIcon from '@mui/icons-material/Business'
import PlaceIcon from '@mui/icons-material/Place'

import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'

import { uncrewedDutyLegendItem } from './globalRosterUtils'

// A PTS duty with no crew shifts, drawn on the week grid like a shift card
// (dashed, in the "uncovered" palette). Clicking opens the duty on the PTS
// planning page for its date.
const RosterDutyCard = ({
  duty,
  continuesFromPrior = false,
  continuesToNext = false,
  visibleSpan = 1,
  onClick,
}) => {
  const style = uncrewedDutyLegendItem
  const isMultiDay = !moment(duty.start).isSame(moment(duty.end), 'day')
  const timeFormat = isMultiDay && visibleSpan > 1 ? 'ddd HH:mm' : 'HH:mm'
  const fullRange = `${moment(duty.start).format('ddd D MMM HH:mm')} – ${moment(
    duty.end,
  ).format('ddd D MMM HH:mm')}`
  const vehicleLabel = duty.vehicle?.callSign
    ? [duty.vehicle.callSign, duty.vehicle.regPlate].filter(Boolean).join(' · ')
    : duty.vehicle?.regPlate || 'No vehicle'

  return (
    <Tooltip title={`${fullRange} · open this duty on the PTS planning page`}>
      <Box
        onClick={onClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onClick?.()
          }
        }}
        sx={{
          px: 1,
          py: 0.75,
          borderRadius: 1,
          bgcolor: style.bg,
          color: style.fg,
          cursor: 'pointer',
          border: '1.5px dashed',
          borderColor: style.border,
          '&:hover': { boxShadow: 2, filter: 'brightness(0.98)' },
          '&:focus-visible': { outline: `2px solid ${style.border}`, outlineOffset: 1 },
        }}
      >
        <Stack direction="row" spacing={0.5} alignItems="center">
          <AirportShuttleIcon fontSize="small" sx={{ color: style.fg }} />
          <Typography variant="caption" fontWeight={700} color="inherit" noWrap>
            {continuesFromPrior ? '← ' : ''}
            {moment(duty.start).format(timeFormat)}-{moment(duty.end).format(timeFormat)}
            {continuesToNext ? ' →' : ''}
          </Typography>
        </Stack>
        <Typography variant="caption" display="block" noWrap color="inherit" fontWeight={700}>
          {duty.dutyRef ? `PTS duty ${duty.dutyRef}` : 'PTS duty'}
        </Typography>
        <Stack direction="row" gap={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
          <Chip
            size="small"
            label="No crew yet"
            sx={{ height: 20, bgcolor: style.fg, color: style.bg, fontWeight: 700 }}
          />
          <Chip
            size="small"
            variant="outlined"
            label={vehicleLabel}
            sx={{ height: 20, bgcolor: style.chipBg, color: style.fg, borderColor: style.border }}
          />
          {duty.organisation?.orgName && (
            <Chip
              size="small"
              variant="outlined"
              icon={<BusinessIcon />}
              label={duty.organisation.orgName}
              sx={{
                height: 20,
                bgcolor: style.chipBg,
                color: style.fg,
                borderColor: style.border,
                '& .MuiChip-icon': { color: style.fg },
              }}
            />
          )}
          {duty.baseLocation?.name && (
            <Chip
              size="small"
              variant="outlined"
              icon={<PlaceIcon />}
              label={duty.baseLocation.name}
              sx={{
                height: 20,
                bgcolor: style.chipBg,
                color: style.fg,
                borderColor: style.border,
                '& .MuiChip-icon': { color: style.fg },
              }}
            />
          )}
        </Stack>
      </Box>
    </Tooltip>
  )
}

export default RosterDutyCard
